<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'ClientMarketingCampaignOverview' }" class="btn btn-secondary">
                  <i class="ri-bar-chart-grouped-line"></i>
                  跨活動成效
                </router-link>
                <router-link :to="{ name: 'SmartMarketingList' }" class="btn btn-secondary">
                  <i class="ri-sparkling-line"></i>
                  智能行銷方案
                </router-link>
                <router-link :to="{ name: 'CreateMarketingCampaignFromPlaybook' }" class="btn btn-secondary">
                  <i class="ri-magic-line"></i>
                  從劇本建立
                </router-link>
                <router-link :to="{ name: 'CreateMarketingCampaign' }" class="btn btn-primary">
                  <i class="ri-add-line"></i>
                  新增空白活動
                </router-link>
      </div>
    </div>

    <div class="page-container">
    <div class="create-guide">
      <div class="create-guide-item">
        <h3 class="create-guide-title">
          <i class="ri-magic-line"></i>
          從劇本建立
        </h3>
        <p>
          選擇內建行銷模板（如：新好友歡迎、註冊禮、沉睡喚回、滿額回饋），系統會自動建立觸發條件與動作步驟，您只需在編輯器綁定推播範本、優惠券等資源後即可啟用。
        </p>
        <p class="create-guide-tip">適合：常見行銷場景、想快速上線</p>
      </div>
      <div class="create-guide-item">
        <h3 class="create-guide-title">
          <i class="ri-add-line"></i>
          新增空白活動
        </h3>
        <p>
          只建立活動名稱與期間等基本資料，不會預先產生任何流程步驟。建立後需自行新增觸發事件（如加好友、註冊、消費）與對應動作（推播、發券、發點）。
        </p>
        <p class="create-guide-tip">適合：特殊流程、現有劇本模板都不符合需求</p>
      </div>
    </div>

    <div class="info-banner">
      <i class="ri-information-line"></i>
      <div class="info-content">
        <strong>不確定從哪開始？</strong>
        可先至「智能行銷方案」查看系統依會員數據推薦的行銷場景，再一鍵以對應劇本建立活動。兩種建立方式完成後都會先產生<strong>草稿</strong>，確認流程與資源無誤後再啟用。
      </div>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋活動名稱或代碼..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="draft">草稿</option>
          <option value="active">啟用</option>
          <option value="inactive">停用</option>
          <option value="ended">已結束</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filterSource" class="form-control" @change="handleFilterChange">
          <option value="">全部來源</option>
          <option value="manual">手動建立</option>
          <option value="smart_marketing">智能行銷</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchCampaigns">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>活動代碼</th>
            <th>活動名稱</th>
            <th>狀態</th>
            <th>來源</th>
            <th>劇本</th>
            <th>活動期間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="campaigns.length === 0">
            <td colspan="7" class="empty-state">尚無行銷活動流程</td>
          </tr>
          <tr v-for="campaign in campaigns" :key="campaign.id">
            <td>{{ campaign.campaignCode }}</td>
            <td>{{ campaign.campaignName }}</td>
            <td>
              <span :class="statusBadgeClass(campaign.status)">{{ statusLabel(campaign.status) }}</span>
            </td>
            <td>{{ sourceLabel(campaign.creationSource) }}</td>
            <td>{{ playbookLabel(campaign.playbookCode) }}</td>
            <td>{{ formatPeriod(campaign.startAt, campaign.endAt) }}</td>
            <td>
              <div class="action-buttons">
                <router-link
                  :to="{ name: 'EditMarketingCampaign', params: { id: campaign.id } }"
                  class="btn btn-sm btn-primary"
                >
                  <i class="ri-edit-line"></i>
                  編輯
                </router-link>
                <router-link
                  v-if="campaign.status !== 'draft'"
                  :to="{ name: 'EditMarketingCampaign', params: { id: campaign.id }, query: { tab: 'analytics' } }"
                  class="btn btn-sm btn-secondary"
                >
                  <i class="ri-bar-chart-box-line"></i>
                  成效
                </router-link>
                <button
                  type="button"
                  class="btn btn-sm btn-danger"
                  @click.stop="handleDelete(campaign)"
                >
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="currentPage"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="goToPage"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { marketingCampaignService } from '@/api/modules/marketingCampaigns.js';
import { getPlaybookLabel } from '@/constants/marketingCampaign.js';
import { showDeleteConfirmDeferred, showError, showSuccess, fireSwalDeferred } from '@/utils/swal.js';

export default {
  name: 'MarketingCampaignList',
  setup() {
    const { pageTitle } = usePageTitle('行銷活動流程');
    const campaigns = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const filterSource = ref('');
    const currentPage = ref(1);
    const pagination = reactive({ totalPages: 1, totalItems: 0 });

    const fetchCampaigns = async () => {
      loading.value = true;
      error.value = null;
      try {
        const params = { page: currentPage.value, pageSize: 20 };
        if (searchQuery.value.trim()) params.search = searchQuery.value.trim();
        if (filterStatus.value) params.status = filterStatus.value;
        if (filterSource.value) params.creationSource = filterSource.value;

        const res = await marketingCampaignService.getCampaigns(params);
        const data = res.data?.data ?? res.data;
        campaigns.value = data?.items || [];
        Object.assign(pagination, data?.pagination || {});
      } catch (e) {
        error.value = e.response?.data?.message || e.message || '載入失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchCampaigns();
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchCampaigns();
    };

    const goToPage = (p) => {
      currentPage.value = p;
      fetchCampaigns();
    };

    const formatPeriod = (start, end) => {
      const fmt = (v) => new Date(v).toLocaleString('zh-TW');
      return `${fmt(start)} ~ ${fmt(end)}`;
    };

    const statusLabel = (status) => ({
      draft: '草稿',
      active: '啟用',
      inactive: '停用',
      ended: '已結束'
    }[status] || status);

    const statusBadgeClass = (status) => ({
      draft: 'badge badge-secondary',
      active: 'badge badge-success',
      inactive: 'badge badge-warning',
      ended: 'badge badge-secondary'
    }[status] || 'badge badge-secondary');

    const sourceLabel = (source) => ({
      manual: '手動',
      smart_marketing: '智能行銷'
    }[source] || source || '-');

    const handleDelete = async (campaign) => {
      try {
        if (campaign.status === 'draft') {
          const ok = await showDeleteConfirmDeferred(campaign.campaignName);
          if (!ok) return;
          await marketingCampaignService.deleteCampaign(campaign.id, { deleteData: true });
          showSuccess('草稿已刪除');
          await fetchCampaigns();
          return;
        }

        const impactRes = await marketingCampaignService.getDeleteImpact(campaign.id);
        const impact = impactRes.data?.data ?? impactRes.data;

        if (!impact?.hasData) {
          const ok = await showDeleteConfirmDeferred(campaign.campaignName);
          if (!ok) return;
          await marketingCampaignService.deleteCampaign(campaign.id, { deleteData: true });
          showSuccess('已刪除');
          await fetchCampaigns();
          return;
        }

        const decision = await fireSwalDeferred({
          icon: 'warning',
          title: '此活動已有數據',
          html: `
            <div style="text-align:left;line-height:1.6">
              <div>參與者：${impact.counts?.participants ?? 0}</div>
              <div>事件記錄：${impact.counts?.eventLogs ?? 0}</div>
              <div>動作記錄：${impact.counts?.actionLogs ?? 0}</div>
              <div>歸因記錄：${impact.counts?.orderAttributions ?? 0}</div>
              <br/>
              <div>請選擇刪除方式：</div>
            </div>
          `,
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: '保留數據（保留 1 年）',
          denyButtonText: '活動與數據都刪除',
          cancelButtonText: '取消',
          confirmButtonColor: '#0d6efd',
          denyButtonColor: '#dc3545',
          cancelButtonColor: '#6c757d'
        });

        if (decision.isDismissed) return;

        const deleteData = decision.isDenied;
        await marketingCampaignService.deleteCampaign(campaign.id, { deleteData });
        showSuccess(deleteData ? '活動與數據已刪除' : '活動已刪除（數據保留 1 年）');
        await fetchCampaigns();
      } catch (e) {
        showError(e.response?.data?.message || e.message || '刪除失敗');
      }
    };

    const playbookLabel = getPlaybookLabel;

    onMounted(fetchCampaigns);

    return {
      pageTitle,
      campaigns,
      loading,
      error,
      searchQuery,
      filterStatus,
      filterSource,
      currentPage,
      pagination,
      fetchCampaigns,
      handleSearch,
      handleFilterChange,
      goToPage,
      formatPeriod,
      statusLabel,
      statusBadgeClass,
      sourceLabel,
      playbookLabel,
      handleDelete
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';
</style>

<style scoped>
.create-guide {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.create-guide-item {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
}

.create-guide-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.create-guide-title i {
  color: #667eea;
}

.create-guide-item p {
  margin: 0 0 8px;
  font-size: 14px;
  color: #555;
  line-height: 1.6;
}

.create-guide-tip {
  margin: 0;
  font-size: 13px;
  color: #667eea;
  font-weight: 500;
}

.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-left: 4px solid #3b82f6;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #1e40af;
  line-height: 1.6;
}

.info-banner i {
  font-size: 18px;
  color: #3b82f6;
  flex-shrink: 0;
  margin-top: 1px;
}

.info-content strong {
  font-weight: 600;
}

@media (max-width: 768px) {
  .create-guide {
    grid-template-columns: 1fr;
  }
}

.table-wrapper table {
  min-width: 960px;
}

.badge-warning {
  background: #fff3cd;
  color: #856404;
}
</style>
